/**
 * Previous/next buttons for the influencer carousel (sections/sicaly-influencers.liquid).
 * The track itself is a native scroll-snap list, so swipe, trackpad and keyboard scrolling
 * work without this script; the buttons stay hidden until it runs and there is overflow.
 */
class SicalyInfluencerCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.controls = this.querySelector('[data-controls]');
    this.previous = this.querySelector('[data-previous]');
    this.next = this.querySelector('[data-next]');
    if (!this.track || !this.controls) return;

    this.update = this.update.bind(this);
    // aria-disabled instead of disabled so keyboard focus stays on the button at either end
    this.previous.addEventListener('click', () => this.go(this.previous, -1));
    this.next.addEventListener('click', () => this.go(this.next, 1));
    this.track.addEventListener('scroll', this.update, { passive: true });

    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.track?.removeEventListener('scroll', this.update);
  }

  get isRtl() {
    return getComputedStyle(this.track).direction === 'rtl';
  }

  go(button, direction) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    const slide = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = slide ? slide.getBoundingClientRect().width + gap : this.track.clientWidth;
    // Move by as many whole slides as fit, so the next unseen photo lands at the start
    const slides = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.track.scrollBy({
      left: direction * slides * step * (this.isRtl ? -1 : 1),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  update() {
    const { scrollWidth, clientWidth } = this.track;
    const scrollLeft = Math.abs(this.track.scrollLeft);
    const overflows = scrollWidth - clientWidth > 1;

    this.controls.hidden = !overflows;
    this.previous.setAttribute('aria-disabled', String(scrollLeft <= 1));
    this.next.setAttribute('aria-disabled', String(scrollLeft + clientWidth >= scrollWidth - 1));
  }
}

if (!customElements.get('sicaly-influencer-carousel')) {
  customElements.define('sicaly-influencer-carousel', SicalyInfluencerCarousel);
}
